import React, { useEffect } from "react";

import {
  SessionCardContainer,
  SessionCardBottomBar,
} from "./SessionCard.styles";
import {
  ImageBox,
  MeshGradient,
  MeshColours,
  SessionDetails,
  MessageRibbonOverlay,
} from "..";
import {
  SessionInterface,
  SessionDetailsInterface,
  BookingInterestSessionInterface,
} from "../../models/Session";
import { Countdown } from "..";
import { SelectButton } from "..";
import dayjs from "dayjs";
import { formatSessionDate, safeTz } from "../../utils/dates";
import { isSessionInLastMinPhase } from "../../utils/sessionUtils";
import { daysToGoWarning } from "../../utils/constants";

const notUndefinedOrNull = (value: any) => {
  return value != undefined && value != null;
};

export const generateSessionDetails = (
  session: BookingInterestSessionInterface | SessionInterface
) => {
  const tz = session?.shootingLocation?.timeZone;
  const date = formatSessionDate(session?.showStartTime, tz);
  const arrivalTime = tz
    ? safeTz(session?.arrivalTime!, tz).format("h:mm A")
    : dayjs(session?.arrivalTime!).format("h:mm A");
  const approxFinishTime = tz
    ? safeTz(session?.approxFinishTime!, tz).format("h:mm A")
    : dayjs(session?.approxFinishTime!).format("h:mm A");
  const locationName = session?.shootingLocation?.name ?? "";

  const queryWithPlaceId = session?.shootingLocation?.placeId
    ? "&query_place_id=" + session?.shootingLocation?.placeId
    : "";

  const locationLink = `https://www.google.com/maps/search/?api=1&query=${session?.shootingLocation?.latitude},${session?.shootingLocation?.longitude}${queryWithPlaceId}`;

  const locationObj =
    locationName &&
    notUndefinedOrNull(session.shootingLocation?.latitude) &&
    notUndefinedOrNull(session.shootingLocation?.latitude)
      ? {
          name: locationName,
          link: locationLink,
        }
      : null;

  const sessionDetails: SessionDetailsInterface = {
    name: session?.name,
    date: date,
    arrivalTime: arrivalTime,
    approxFinishTime: approxFinishTime,
    location: locationObj,
    minimumAgeRequirement: session?.overrideMinimumAgeRequirement,
  };

  return sessionDetails;
};

export interface CustomSessionCardProps {
  showName?: string | null;
  showMinimumAge?: number | null;
  session?: SessionInterface | null;
  handleClick?: Function;
  isDeletable?: boolean;
  selectedSessionId: number | undefined;
  colour?: MeshColours;
  isPriority?: boolean;
  isPriorityFull?: boolean;
}

export const SessionCard = ({
  showName = null,
  session = null,
  handleClick,
  isDeletable = false,
  colour = "pink-blue-teal",
  showMinimumAge,
  selectedSessionId,
  isPriority = false,
  isPriorityFull = false,
  ...rest
}: CustomSessionCardProps) => {
  const [sessionDetails, setSessionDetails] =
    React.useState<SessionDetailsInterface>({
      name: "",
      date: "",
      arrivalTime: "",
      approxFinishTime: "",
      location: null,
    });
  const sessionDate = dayjs(session?.arrivalTime!);

  useEffect(() => {
    if (session) {
      const sessionDetailsObj = generateSessionDetails(
        session as BookingInterestSessionInterface
      );
      setSessionDetails(sessionDetailsObj);
    }
  }, [session]);

  // Consolidated flow (TTT-688 §5.2): once the last-min phase has started, a full session
  // invites new visitors onto the stand by list instead of just saying it is full.
  const isLastMinPhase = isSessionInLastMinPhase(session);

  const ribbonMessage = session?.isRibbonMessageActive
    ? session?.ribbonMessage
    : isLastMinPhase
      ? "Full - join stand by list"
      : "Full - Waitlist only";

  return (
    <SessionCardContainer
      isPriority={isPriority}
      data-cy="session-card"
      data-id={session?.id}
      onClick={() => {
        if (handleClick) handleClick();
      }}
      {...rest}
    >
      {((ribbonMessage && session?.isRibbonMessageActive) ||
        session?.isFullyBookedRibbonActive) && (
        <MessageRibbonOverlay message={ribbonMessage} />
      )}
      {isPriorityFull && !session?.isRibbonMessageActive && !session?.isFullyBookedRibbonActive && (
        <MessageRibbonOverlay message={"Priority seats fully allocated\nApply for regular tickets"} variant="subtle" />
      )}
      <ImageBox
        variant="sessionCard"
        src={session?.imageUrl ?? ""}
        alt={`${session?.name} Image`}
        showLogoFallback={false}
      />
      <SessionDetails
        isPriority={isPriority}
        showName={showName}
        showMinimumAge={showMinimumAge}
        sessionDetails={sessionDetails}
      />
      <SessionCardBottomBar>
        <Countdown dueDate={sessionDate} daysToGoWarning={daysToGoWarning} isLiveCountDown={false} />
        <SelectButton
          gradientStyle={isPriority ? "priority" : "gradient4"}
          sessionId={session?.id}
          selectedSessionId={selectedSessionId}
        >
          {isPriority ? "Select Priority" : "Select"}
        </SelectButton>
      </SessionCardBottomBar>
      <MeshGradient
        colour={colour}
        position="bottom"
        size="low"
        intensity="high"
        sx={{ zIndex: 0 }}
      />
    </SessionCardContainer>
  );
};

export interface SessionCardProps
  extends React.ComponentProps<typeof SessionCard> {
  as?: React.ElementType;
}

SessionCard.displayName = "SessionCard";
